import { useEffect, useState } from "react";
import { WarningCircle, XCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import client from "../lib/api";

/**
 * Full-width inline warning banner shown when a dispatcher is about to book
 * a load from a customer/broker that has amber or red risk. Silent when the
 * customer is green (never seen before OR always pays on time) — silence is
 * a positive signal, no visual noise.
 *
 * Uses the same shared credit-score cache exposed by CustomerCreditBadge.jsx,
 * so this component makes zero extra API calls when the badge is already on
 * the page (or was recently rendered in this session).
 */

// Import the shared cache from the badge module by re-fetching (5-min TTL
// makes this cheap). We deliberately don't share the cache variable across
// files to avoid a tight coupling between two independent components — the
// browser HTTP cache + 5-min TTL means this is a no-op in practice.

const CACHE_TTL_MS = 5 * 60 * 1000;
let _cache = null;
let _pending = null;

async function _getScoreFor(name) {
  const key = (name || "").trim().toLowerCase();
  if (!key) return null;
  if (_cache && Date.now() - _cache.ts < CACHE_TTL_MS) {
    return _cache.byName.get(key) || null;
  }
  if (_pending) {
    const c = await _pending;
    return c.byName.get(key) || null;
  }
  _pending = (async () => {
    try {
      const { data } = await client.get("/customers/credit-scores");
      const byName = new Map();
      for (const c of data.customers || []) {
        byName.set((c.customer || "").trim().toLowerCase(), c);
      }
      _cache = { ts: Date.now(), byName };
      return _cache;
    } catch {
      _cache = { ts: Date.now(), byName: new Map() };
      return _cache;
    } finally {
      _pending = null;
    }
  })();
  const c = await _pending;
  return c.byName.get(key) || null;
}

const money = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function PastedLoadRiskWarning({ customerName }) {
  const [score, setScore] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!customerName || !customerName.trim()) {
      setScore(null);
      setChecked(true);
      return;
    }
    setChecked(false);
    (async () => {
      const s = await _getScoreFor(customerName);
      if (!cancelled) {
        setScore(s);
        setChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [customerName]);

  if (!checked) return null;                              // still loading
  if (!score) return null;                                // customer not in system — silent
  if (score.band === "green") return null;                // green = no warning
  if (score.band !== "amber" && score.band !== "red") return null;

  const isRed = score.band === "red";
  const meta = isRed
    ? {
        Icon: XCircle,
        title: "HIGH RISK broker",
        tone: "text-rose-100",
        subTone: "text-rose-200/80",
        border: "border-rose-500/50",
        bg: "bg-rose-500/10",
        iconTone: "text-rose-300",
        actionTone: "text-rose-300 hover:text-rose-200",
      }
    : {
        Icon: WarningCircle,
        title: "Watch — slow payer",
        tone: "text-amber-100",
        subTone: "text-amber-200/80",
        border: "border-amber-500/50",
        bg: "bg-amber-500/10",
        iconTone: "text-amber-300",
        actionTone: "text-amber-300 hover:text-amber-200",
      };
  const Icon = meta.Icon;

  // Build a punchy one-line summary + a longer stat line
  const openInvoices = score.invoice_count_outstanding || 0;
  const outstanding = score.total_outstanding || 0;
  const avg = score.avg_days_to_pay;
  const oldest = score.oldest_outstanding_days || 0;

  const oneLiner =
    (openInvoices > 0 && outstanding > 0)
      ? `${openInvoices} unpaid invoice${openInvoices !== 1 ? "s" : ""} totaling ${money(outstanding)}`
      : (avg !== null && avg > 15)
        ? `Pays ${Math.round(avg)}d after due on average`
        : (oldest > 30)
          ? `Oldest outstanding ${oldest}d overdue`
          : "Has payment history issues";

  const statBits = [];
  if (avg !== null) {
    statBits.push(avg < 0 ? `Avg pay: ${Math.abs(Math.round(avg))}d early`
                          : `Avg pay: ${Math.round(avg)}d late`);
  }
  if (oldest > 0) statBits.push(`Oldest overdue: ${oldest}d`);
  if (score.reminder_count > 0) {
    statBits.push(`${score.reminder_count} reminder${score.reminder_count !== 1 ? "s" : ""} sent`);
  }

  return (
    <div
      data-testid={`paste-load-risk-warning-${score.band}`}
      className={`rounded-lg border ${meta.border} ${meta.bg} p-3 my-3`}
    >
      <div className="flex items-start gap-2">
        <Icon size={18} className={`${meta.iconTone} flex-shrink-0 mt-0.5`} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest font-bold ${meta.iconTone}`}>
              {meta.title}
            </span>
            <span className={`text-xs ${meta.tone} font-medium truncate`}>{customerName}</span>
          </div>
          <div className={`text-sm ${meta.tone} font-semibold mt-1`}>{oneLiner}</div>
          {statBits.length > 0 && (
            <div className={`text-[11px] ${meta.subTone} mt-1`}>
              {statBits.join(" · ")}
            </div>
          )}
          <Link
            to="/app/customer-credit"
            data-testid="paste-load-risk-view-full"
            className={`inline-flex items-center gap-1 text-[11px] font-medium ${meta.actionTone} mt-2`}
          >
            View full credit history
            <ArrowSquareOut size={11} />
          </Link>
        </div>
      </div>
    </div>
  );
}
